import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  FaHome,
  FaPlus,
  FaTrash,
  FaSave,
  FaUndo,
  FaArrowUp,
  FaArrowDown,
  FaUpload
} from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const HomeCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [activeTab, setActiveTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    hero: {
      heroLine1: '',
      typewriterStrings: [],
      heroBody: '',
      ctaPrimary: '',
      ctaSecondary: '',
      ctaPrimaryLink: '',
      ctaSecondaryLink: ''
    },
    servicesSection: {
      servicesTitle: '',
      servicesSubtitle: '',
      services: []
    },
    advantageSection: {
      whyChooseTitle: '',
      whyChooseSubtitle: '',
      features: []
    },
    reviewSection: {
      reviewsTitle: '',
      reviews: []
    },
    approachSection: {
      approachTitle: '',
      approachSubtitle: '',
      approachParagraphs: [],
      highlights: []
    },
    integrationsSection: {
      integrationsTitle: '',
      integrationsSubtitle: '',
      logosRow1: [],
      logosRow2: []
    },
    faqSection: {
      faqsTitle: '',
      faqsSubtitle: '',
      faqs: []
    },
    isPublished: true
  });

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/home/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const d = res.data.data || {};
        setForm({
          hero: {
            heroLine1: d.hero?.heroLine1 || '',
            typewriterStrings: d.hero?.typewriterStrings || [],
            heroBody: d.hero?.heroBody || '',
            ctaPrimary: d.hero?.ctaPrimary || '',
            ctaSecondary: d.hero?.ctaSecondary || '',
            ctaPrimaryLink: d.hero?.ctaPrimaryLink || '',
            ctaSecondaryLink: d.hero?.ctaSecondaryLink || ''
          },
          servicesSection: d.servicesSection || { servicesTitle: '', servicesSubtitle: '', services: [] },
          advantageSection: d.advantageSection || { whyChooseTitle: '', whyChooseSubtitle: '', features: [] },
          reviewSection: d.reviewSection || { reviewsTitle: '', reviews: [] },
          approachSection: d.approachSection || { approachTitle: '', approachSubtitle: '', approachParagraphs: [], highlights: [] },
          integrationsSection: d.integrationsSection || { integrationsTitle: '', integrationsSubtitle: '', logosRow1: [], logosRow2: [] },
          faqSection: d.faqSection || { faqsTitle: '', faqsSubtitle: '', faqs: [] },
          isPublished: d.isPublished !== false
        });
      }
    } catch (err) {
      toastError('Failed to load Home page content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/home/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Home page content saved successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreDefaults = async () => {
    if (!window.confirm('Are you sure you want to revert Home page content to default?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/home/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Restored default content successfully');
        fetchContent();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to restore defaults');
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for Hero Typewriter strings
  const updateTypewriterString = (idx, val) => {
    setForm(prev => {
      const list = [...prev.hero.typewriterStrings];
      list[idx] = val;
      return {
        ...prev,
        hero: { ...prev.hero, typewriterStrings: list }
      };
    });
  };

  const addTypewriterString = () => {
    setForm(prev => ({
      ...prev,
      hero: { ...prev.hero, typewriterStrings: [...prev.hero.typewriterStrings, ''] }
    }));
  };

  const removeTypewriterString = (idx) => {
    setForm(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        typewriterStrings: prev.hero.typewriterStrings.filter((_, i) => i !== idx)
      }
    }));
  };

  // Helper functions for Services
  const addService = () => {
    setForm(prev => ({
      ...prev,
      servicesSection: {
        ...prev.servicesSection,
        services: [
          ...prev.servicesSection.services,
          {
            title: '',
            description: '',
            linkText: 'Learn More',
            link: '',
            iconName: 'building',
            gradient: 'from-[#2563eb] to-[#1d4ed8]',
            glowColor: 'rgba(37,99,235,0.12)',
            accentColor: '#2563eb',
            vectorName: 'vectorBookkeeping',
            isActive: true,
            order: prev.servicesSection.services.length
          }
        ]
      }
    }));
  };

  const removeService = (idx) => {
    setForm(prev => ({
      ...prev,
      servicesSection: {
        ...prev.servicesSection,
        services: prev.servicesSection.services.filter((_, i) => i !== idx)
      }
    }));
  };

  const updateServiceField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.servicesSection.services];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        servicesSection: { ...prev.servicesSection, services: list }
      };
    });
  };

  // Helper functions for Advantage Features
  const addFeature = () => {
    setForm(prev => ({
      ...prev,
      advantageSection: {
        ...prev.advantageSection,
        features: [
          ...prev.advantageSection.features,
          {
            icon: '📊',
            title: '',
            description: '',
            accentFrom: '#2563eb',
            accentTo: '#3b82f6',
            isActive: true,
            order: prev.advantageSection.features.length
          }
        ]
      }
    }));
  };

  const removeFeature = (idx) => {
    setForm(prev => ({
      ...prev,
      advantageSection: {
        ...prev.advantageSection,
        features: prev.advantageSection.features.filter((_, i) => i !== idx)
      }
    }));
  };

  const updateFeatureField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.advantageSection.features];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        advantageSection: { ...prev.advantageSection, features: list }
      };
    });
  };

  // Helper functions for Reviews
  const addReview = () => {
    setForm(prev => ({
      ...prev,
      reviewSection: {
        ...prev.reviewSection,
        reviews: [
          ...prev.reviewSection.reviews,
          {
            text: '',
            author_name: '',
            company_name: '',
            rating: 5,
            source: 'Google',
            initials: '',
            color: '#2563eb',
            isActive: true,
            order: prev.reviewSection.reviews.length
          }
        ]
      }
    }));
  };

  const removeReview = (idx) => {
    setForm(prev => ({
      ...prev,
      reviewSection: {
        ...prev.reviewSection,
        reviews: prev.reviewSection.reviews.filter((_, i) => i !== idx)
      }
    }));
  };

  const updateReviewField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.reviewSection.reviews];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        reviewSection: { ...prev.reviewSection, reviews: list }
      };
    });
  };

  // Helper functions for Approach Section
  const updateApproachParagraph = (idx, val) => {
    setForm(prev => {
      const list = [...prev.approachSection.approachParagraphs];
      list[idx] = val;
      return {
        ...prev,
        approachSection: { ...prev.approachSection, approachParagraphs: list }
      };
    });
  };

  const addApproachParagraph = () => {
    setForm(prev => ({
      ...prev,
      approachSection: {
        ...prev.approachSection,
        approachParagraphs: [...prev.approachSection.approachParagraphs, '']
      }
    }));
  };

  const removeApproachParagraph = (idx) => {
    setForm(prev => ({
      ...prev,
      approachSection: {
        ...prev.approachSection,
        approachParagraphs: prev.approachSection.approachParagraphs.filter((_, i) => i !== idx)
      }
    }));
  };

  const addApproachHighlight = () => {
    setForm(prev => ({
      ...prev,
      approachSection: {
        ...prev.approachSection,
        highlights: [
          ...prev.approachSection.highlights,
          {
            text: '',
            icon: '👨‍💼',
            accent: '#2563eb',
            isActive: true,
            order: prev.approachSection.highlights.length
          }
        ]
      }
    }));
  };

  const removeApproachHighlight = (idx) => {
    setForm(prev => ({
      ...prev,
      approachSection: {
        ...prev.approachSection,
        highlights: prev.approachSection.highlights.filter((_, i) => i !== idx)
      }
    }));
  };

  const updateApproachHighlightField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.approachSection.highlights];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        approachSection: { ...prev.approachSection, highlights: list }
      };
    });
  };

  // Helper functions for FAQs
  const addFAQ = () => {
    setForm(prev => ({
      ...prev,
      faqSection: {
        ...prev.faqSection,
        faqs: [
          ...prev.faqSection.faqs,
          {
            question: '',
            answer: '',
            isActive: true,
            order: prev.faqSection.faqs.length
          }
        ]
      }
    }));
  };

  const removeFAQ = (idx) => {
    setForm(prev => ({
      ...prev,
      faqSection: {
        ...prev.faqSection,
        faqs: prev.faqSection.faqs.filter((_, i) => i !== idx)
      }
    }));
  };

  const updateFAQField = (idx, key, val) => {
    setForm(prev => {
      const list = [...prev.faqSection.faqs];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        faqSection: { ...prev.faqSection, faqs: list }
      };
    });
  };
  // Helper functions for Logos integrations
  const addLogo = (rowKey) => {
    setForm(prev => ({
      ...prev,
      integrationsSection: {
        ...prev.integrationsSection,
        [rowKey]: [
          ...(prev.integrationsSection[rowKey] || []),
          { src: '', alt: '', order: (prev.integrationsSection[rowKey] || []).length }
        ]
      }
    }));
  };

  const removeLogo = (rowKey, idx) => {
    if (!window.confirm('Remove this logo?')) return;
    setForm(prev => ({
      ...prev,
      integrationsSection: {
        ...prev.integrationsSection,
        [rowKey]: prev.integrationsSection[rowKey].filter((_, i) => i !== idx)
      }
    }));
  };

  const updateLogoField = (rowKey, idx, key, val) => {
    setForm(prev => {
      const list = [...prev.integrationsSection[rowKey]];
      list[idx] = { ...list[idx], [key]: val };
      return {
        ...prev,
        integrationsSection: {
          ...prev.integrationsSection,
          [rowKey]: list
        }
      };
    });
  };

  const handleLogoUpload = async (e, rowKey, idx) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/home/admin/upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        }
      );
      if (res.data.success) {
        updateLogoField(rowKey, idx, 'src', res.data.url);
        toastSuccess('Logo uploaded and optimized successfully');
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to upload logo');
    }
  };


  // Order sorting helpers
  const moveItem = (sectionKey, itemKey, index, direction) => {
    setForm(prev => {
      const section = prev[sectionKey];
      const list = [...section[itemKey]];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= list.length) return prev;

      // Swap orders
      const tempOrder = list[index].order;
      list[index].order = list[newIndex].order;
      list[newIndex].order = tempOrder;

      // Swap positions
      const temp = list[index];
      list[index] = list[newIndex];
      list[newIndex] = temp;

      return {
        ...prev,
        [sectionKey]: {
          ...section,
          [itemKey]: list
        }
      };
    });
  };

  if (loading) return <SkeletonLoader variant="page" />;

  return (
    <PageLayout>
      <PageHeader
        title="Home Page CMS"
        description="Configure dynamic headings, typewriter text, service cards, testimonials, and FAQs for the landing page."
        icon={FaHome}
        actions={
          <div className="flex gap-3">
            <button
              onClick={handleRestoreDefaults}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 dark:border-dark-600 rounded-xl text-sm font-medium hover:bg-gray-50 dark:hover:bg-dark-700 flex items-center gap-2"
            >
              <FaUndo className="w-3.5 h-3.5" /> Revert Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-premium flex items-center gap-2"
            >
              <FaSave className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-dark-700 overflow-x-auto gap-2 mb-6">
        {[
          { id: 'hero', name: 'Hero Banner' },
          { id: 'services', name: 'Our Services' },
          { id: 'advantage', name: 'Advantage Details' },
          { id: 'reviews', name: 'Testimonials' },
          { id: 'approach', name: 'Approach Details' },
          { id: 'integrations', name: 'Integrations logo' },
          { id: 'faqs', name: 'Landing FAQs' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 font-bold text-sm border-b-2 whitespace-nowrap transition-all ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {/* Tab 1: Hero Banner */}
        {activeTab === 'hero' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Hero Header configuration</h2>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isPublished}
                  onChange={(e) => setForm(prev => ({ ...prev, isPublished: e.target.checked }))}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                Published
              </label>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Main Title (Line 1)</label>
              <input
                className="input-modern"
                value={form.hero.heroLine1}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  hero: { ...prev.hero, heroLine1: e.target.value }
                }))}
                placeholder="Grow Your U.S. Business with Trusted"
              />
            </div>

            {/* Typewriter strings */}
            <div className="space-y-3 border border-gray-200 dark:border-dark-750 p-4 rounded-xl">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Typewriter strings</label>
                <button
                  type="button"
                  onClick={addTypewriterString}
                  className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add String
                </button>
              </div>
              <div className="space-y-2">
                {form.hero.typewriterStrings.map((str, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      className="input-modern text-sm py-1.5 px-3 flex-1"
                      value={str}
                      onChange={(e) => updateTypewriterString(idx, e.target.value)}
                      placeholder={`Typewriter text #${idx + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeTypewriterString(idx)}
                      className="text-red-500 p-2 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Hero Description Body (HTML supported)</label>
              <textarea
                className="input-modern min-h-[120px]"
                value={form.hero.heroBody}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  hero: { ...prev.hero, heroBody: e.target.value }
                }))}
                placeholder="Starting and growing a business..."
              />
            </div>

            {/* Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 border-t border-gray-150 dark:border-dark-700 pt-4">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.hero.ctaPrimary}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    hero: { ...prev.hero, ctaPrimary: e.target.value }
                  }))}
                  placeholder="Book a Free Consultation"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Label</label>
                <input
                  className="input-modern text-sm"
                  value={form.hero.ctaSecondary}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    hero: { ...prev.hero, ctaSecondary: e.target.value }
                  }))}
                  placeholder="View Our Pricing"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Primary Button Link / Action</label>
                <input
                  className="input-modern text-sm"
                  value={form.hero.ctaPrimaryLink || ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    hero: { ...prev.hero, ctaPrimaryLink: e.target.value }
                  }))}
                  placeholder="#contact or /pricing or external link"
                />
                <span className="text-[10px] text-gray-400">Use '#contact' to trigger the contact modal popup, or any page route (e.g. /pricing).</span>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Secondary Button Link / Action</label>
                <input
                  className="input-modern text-sm"
                  value={form.hero.ctaSecondaryLink || ''}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    hero: { ...prev.hero, ctaSecondaryLink: e.target.value }
                  }))}
                  placeholder="/pricing or #contact or external link"
                />
                <span className="text-[10px] text-gray-400">Use '#contact' to trigger the contact modal popup, or any page route (e.g. /pricing).</span>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Our Services */}
        {activeTab === 'services' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Smart Solutions cards</h2>
              <button
                onClick={addService}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Service Card
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Services Title</label>
                <input
                  className="input-modern"
                  value={form.servicesSection.servicesTitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    servicesSection: { ...prev.servicesSection, servicesTitle: e.target.value }
                  }))}
                  placeholder="Smart Solutions for U.S. Businesses"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Services Subtitle</label>
                <input
                  className="input-modern"
                  value={form.servicesSection.servicesSubtitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    servicesSection: { ...prev.servicesSection, servicesSubtitle: e.target.value }
                  }))}
                  placeholder="How can BooksComply help your U.S. business..."
                />
              </div>
            </div>

            {/* List of Services */}
            <div className="space-y-5 pt-4">
              {form.servicesSection.services.map((service, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <div className="absolute top-4 right-4 flex gap-1.5">
                    <button
                      onClick={() => moveItem('servicesSection', 'services', idx, 'up')}
                      disabled={idx === 0}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveItem('servicesSection', 'services', idx, 'down')}
                      disabled={idx === form.servicesSection.services.length - 1}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeService(idx)}
                      className="text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Service Card #{idx + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.title}
                        onChange={(e) => updateServiceField(idx, 'title', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Link Destination</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.link}
                        onChange={(e) => updateServiceField(idx, 'link', e.target.value)}
                        placeholder="e.g. /bookkeeping"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Link Text</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.linkText}
                        onChange={(e) => updateServiceField(idx, 'linkText', e.target.value)}
                        placeholder="Learn More"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Accent color hex</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.accentColor}
                        onChange={(e) => updateServiceField(idx, 'accentColor', e.target.value)}
                        placeholder="e.g. #2563eb"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Icon Name</label>
                      <select
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.iconName}
                        onChange={(e) => updateServiceField(idx, 'iconName', e.target.value)}
                      >
                        <option value="building">Building (FaBuilding)</option>
                        <option value="dollar">Dollar (FaFileInvoiceDollar)</option>
                        <option value="book">Book (FaBook)</option>
                        <option value="chart">Chart (FaChartLine)</option>
                        <option value="code">Code (FaLaptopCode)</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Illustration vector Name</label>
                      <select
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.vectorName}
                        onChange={(e) => updateServiceField(idx, 'vectorName', e.target.value)}
                      >
                        <option value="vectorBookkeeping">Bookkeeping Illustration</option>
                        <option value="vectorTax">Tax Illustration</option>
                        <option value="vectorFormation">Formation Illustration</option>
                        <option value="vectorCfo">CFO Illustration</option>
                        <option value="vectorZoho">Zoho Illustration</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Gradient Option</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.gradient}
                        onChange={(e) => updateServiceField(idx, 'gradient', e.target.value)}
                        placeholder="e.g. from-[#2563eb] to-[#1d4ed8]"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Glow Color CSS</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={service.glowColor}
                        onChange={(e) => updateServiceField(idx, 'glowColor', e.target.value)}
                        placeholder="e.g. rgba(37,99,235,0.12)"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                    <textarea
                      className="input-modern text-sm py-1.5 px-3 min-h-[70px]"
                      value={service.description}
                      onChange={(e) => updateServiceField(idx, 'description', e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold pt-1">
                    <input
                      type="checkbox"
                      checked={service.isActive}
                      onChange={(e) => updateServiceField(idx, 'isActive', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Active & visible
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 3: Advantage Details */}
        {activeTab === 'advantage' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Advantage points checklist</h2>
              <button
                onClick={addFeature}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Point
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.advantageSection.whyChooseTitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    advantageSection: { ...prev.advantageSection, whyChooseTitle: e.target.value }
                  }))}
                  placeholder="Why Choose BooksComply?"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.advantageSection.whyChooseSubtitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    advantageSection: { ...prev.advantageSection, whyChooseSubtitle: e.target.value }
                  }))}
                  placeholder="Everything you need..."
                />
              </div>
            </div>

            {/* List of Features */}
            <div className="space-y-5 pt-4">
              {form.advantageSection.features.map((feat, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <div className="absolute top-4 right-4 flex gap-1.5">
                    <button
                      onClick={() => moveItem('advantageSection', 'features', idx, 'up')}
                      disabled={idx === 0}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveItem('advantageSection', 'features', idx, 'down')}
                      disabled={idx === form.advantageSection.features.length - 1}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeFeature(idx)}
                      className="text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Point #{idx + 1}</h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Emoji Icon</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={feat.icon}
                        onChange={(e) => updateFeatureField(idx, 'icon', e.target.value)}
                        placeholder="e.g. 📊"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Accent From</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={feat.accentFrom}
                        onChange={(e) => updateFeatureField(idx, 'accentFrom', e.target.value)}
                        placeholder="e.g. #2563eb"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Accent To</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={feat.accentTo}
                        onChange={(e) => updateFeatureField(idx, 'accentTo', e.target.value)}
                        placeholder="e.g. #3b82f6"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Title</label>
                    <input
                      className="input-modern text-sm py-1.5"
                      value={feat.title}
                      onChange={(e) => updateFeatureField(idx, 'title', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Description</label>
                    <textarea
                      className="input-modern text-sm py-1.5 min-h-[60px]"
                      value={feat.description}
                      onChange={(e) => updateFeatureField(idx, 'description', e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold pt-1">
                    <input
                      type="checkbox"
                      checked={feat.isActive}
                      onChange={(e) => updateFeatureField(idx, 'isActive', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Active & visible
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 4: Testimonials */}
        {activeTab === 'reviews' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Customer Reviews</h2>
              <button
                onClick={addReview}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add Review
              </button>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Testimonials Title</label>
              <input
                className="input-modern"
                value={form.reviewSection.reviewsTitle}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  reviewSection: { ...prev.reviewSection, reviewsTitle: e.target.value }
                }))}
                placeholder="What Our Customers Say"
              />
            </div>

            {/* List of Reviews */}
            <div className="space-y-5 pt-4">
              {form.reviewSection.reviews.map((rev, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <div className="absolute top-4 right-4 flex gap-1.5">
                    <button
                      onClick={() => moveItem('reviewSection', 'reviews', idx, 'up')}
                      disabled={idx === 0}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveItem('reviewSection', 'reviews', idx, 'down')}
                      disabled={idx === form.reviewSection.reviews.length - 1}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeReview(idx)}
                      className="text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">Review #{idx + 1}</h3>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Author Name</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={rev.author_name}
                        onChange={(e) => updateReviewField(idx, 'author_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Company / Subtext</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={rev.company_name}
                        onChange={(e) => updateReviewField(idx, 'company_name', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Initials</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={rev.initials}
                        onChange={(e) => updateReviewField(idx, 'initials', e.target.value)}
                        placeholder="e.g. CD"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Avatar color hex</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={rev.color}
                        onChange={(e) => updateReviewField(idx, 'color', e.target.value)}
                        placeholder="e.g. #2563eb"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Source Platform</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={rev.source}
                        onChange={(e) => updateReviewField(idx, 'source', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Star Rating</label>
                      <input
                        type="number"
                        className="input-modern text-sm py-1.5"
                        value={rev.rating}
                        onChange={(e) => updateReviewField(idx, 'rating', Number(e.target.value))}
                        min="1" max="5"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Review Text</label>
                    <textarea
                      className="input-modern text-sm py-1.5 min-h-[70px]"
                      value={rev.text}
                      onChange={(e) => updateReviewField(idx, 'text', e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold pt-1">
                    <input
                      type="checkbox"
                      checked={rev.isActive}
                      onChange={(e) => updateReviewField(idx, 'isActive', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Active & visible
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 5: Approach Details */}
        {activeTab === 'approach' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Approach & Highlights Configuration</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Approach Title</label>
                <input
                  className="input-modern"
                  value={form.approachSection.approachTitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    approachSection: { ...prev.approachSection, approachTitle: e.target.value }
                  }))}
                  placeholder="We help you grow with experience..."
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Approach Subtitle</label>
                <input
                  className="input-modern"
                  value={form.approachSection.approachSubtitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    approachSection: { ...prev.approachSection, approachSubtitle: e.target.value }
                  }))}
                  placeholder="Our team blends cutting-edge technology..."
                />
              </div>
            </div>

            {/* Approach Paragraphs */}
            <div className="space-y-3 border border-gray-200 dark:border-dark-750 p-4 rounded-xl">
              <div className="flex justify-between items-center">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Approach Body Paragraphs</label>
                <button
                  type="button"
                  onClick={addApproachParagraph}
                  className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add Paragraph
                </button>
              </div>
              <div className="space-y-2">
                {form.approachSection.approachParagraphs.map((para, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <textarea
                      className="input-modern text-sm py-1.5 px-3 flex-1 min-h-[60px]"
                      value={para}
                      onChange={(e) => updateApproachParagraph(idx, e.target.value)}
                      placeholder={`Paragraph #${idx + 1}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeApproachParagraph(idx)}
                      className="text-red-500 p-2 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Highlights list */}
            <div className="space-y-3 border border-gray-200 dark:border-dark-750 p-4 rounded-xl">
              <div className="flex justify-between items-center border-b border-gray-150 pb-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Highlight cards</label>
                <button
                  type="button"
                  onClick={addApproachHighlight}
                  className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add Highlight Card
                </button>
              </div>

              <div className="space-y-4 pt-2">
                {form.approachSection.highlights.map((item, idx) => (
                  <div key={idx} className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl relative space-y-3 bg-gray-50/10">
                    <div className="absolute top-4 right-4 flex gap-1.5">
                      <button
                        onClick={() => moveItem('approachSection', 'highlights', idx, 'up')}
                        disabled={idx === 0}
                        className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => moveItem('approachSection', 'highlights', idx, 'down')}
                        disabled={idx === form.approachSection.highlights.length - 1}
                        className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowDown className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeApproachHighlight(idx)}
                        className="text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>

                    <h4 className="font-bold text-xs text-gray-700 dark:text-slate-300">Card #{idx + 1}</h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Emoji Icon</label>
                        <input
                          className="input-modern text-sm py-1.5"
                          value={item.icon}
                          onChange={(e) => updateApproachHighlightField(idx, 'icon', e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Accent color hex</label>
                        <input
                          className="input-modern text-sm py-1.5"
                          value={item.accent}
                          onChange={(e) => updateApproachHighlightField(idx, 'accent', e.target.value)}
                          placeholder="e.g. #2563eb"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Text</label>
                      <input
                        className="input-modern text-sm py-1.5"
                        value={item.text}
                        onChange={(e) => updateApproachHighlightField(idx, 'text', e.target.value)}
                      />
                    </div>
                    <label className="flex items-center gap-2 text-xs font-semibold">
                      <input
                        type="checkbox"
                        checked={item.isActive}
                        onChange={(e) => updateApproachHighlightField(idx, 'isActive', e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      Active & visible
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 6: Integrations logo */}
        {activeTab === 'integrations' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-dark-700 pb-3">Integrations Heading Configuration</h2>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Integrations Title</label>
              <input
                className="input-modern"
                value={form.integrationsSection.integrationsTitle}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  integrationsSection: { ...prev.integrationsSection, integrationsTitle: e.target.value }
                }))}
                placeholder="We work with lots of apps….Check out some of them!"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">Integrations Subtitle (Optional)</label>
              <input
                className="input-modern"
                value={form.integrationsSection.integrationsSubtitle}
                onChange={(e) => setForm(prev => ({
                  ...prev,
                  integrationsSection: { ...prev.integrationsSection, integrationsSubtitle: e.target.value }
                }))}
              />
            </div>

            {/* Row 1 Logos */}
            <div className="space-y-3 border border-gray-200 dark:border-dark-750 p-4 rounded-xl mt-4">
              <div className="flex justify-between items-center border-b border-gray-150 dark:border-dark-700 pb-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Row 1 Logos (Amazon, Bill, Drake, etc.)</label>
                <button
                  type="button"
                  onClick={() => addLogo('logosRow1')}
                  className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add Logo
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-2">
                {(form.integrationsSection.logosRow1 || []).map((logo, idx) => (
                  <div key={idx} className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl relative space-y-3 bg-gray-50/10 dark:bg-dark-900/5">
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveItem('integrationsSection', 'logosRow1', idx, 'up')}
                        disabled={idx === 0}
                        className="text-gray-500 p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem('integrationsSection', 'logosRow1', idx, 'down')}
                        disabled={idx === form.integrationsSection.logosRow1.length - 1}
                        className="text-gray-500 p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLogo('logosRow1', idx)}
                        className="text-red-500 p-1 rounded border border-red-200 hover:bg-red-50"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>

                    <h4 className="font-bold text-xs text-gray-700 dark:text-slate-300">Logo #{idx + 1}</h4>

                    <div className="space-y-2">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Alt text</label>
                        <input
                          className="input-modern text-xs py-1"
                          value={logo.alt || ''}
                          onChange={(e) => updateLogoField('logosRow1', idx, 'alt', e.target.value)}
                          placeholder="e.g. Stripe"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Logo Image File</label>
                        <div className="flex gap-2 items-center">
                          <input
                            className="input-modern text-xs py-1 flex-1"
                            value={logo.src || ''}
                            onChange={(e) => updateLogoField('logosRow1', idx, 'src', e.target.value)}
                            placeholder="Image URL..."
                          />
                          <label className="flex items-center gap-1.5 px-2 py-1 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                            <FaUpload className="w-2.5 h-2.5" /> Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleLogoUpload(e, 'logosRow1', idx)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>

                      {logo.src && (
                        <div className="relative w-full h-16 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2">
                          <img
                            src={logo.src.startsWith('http') ? logo.src : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${logo.src}`}
                            alt="Logo preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2 Logos */}
            <div className="space-y-3 border border-gray-200 dark:border-dark-750 p-4 rounded-xl mt-4">
              <div className="flex justify-between items-center border-b border-gray-150 dark:border-dark-700 pb-2">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Row 2 Logos (Stripe, Shopify, Xero, etc.)</label>
                <button
                  type="button"
                  onClick={() => addLogo('logosRow2')}
                  className="text-xs font-bold text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  <FaPlus className="w-3 h-3" /> Add Logo
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-2">
                {(form.integrationsSection.logosRow2 || []).map((logo, idx) => (
                  <div key={idx} className="border border-gray-150 dark:border-dark-700 p-4 rounded-xl relative space-y-3 bg-gray-50/10 dark:bg-dark-900/5">
                    <div className="absolute top-2 right-2 flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveItem('integrationsSection', 'logosRow2', idx, 'up')}
                        disabled={idx === 0}
                        className="text-gray-500 p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowUp className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem('integrationsSection', 'logosRow2', idx, 'down')}
                        disabled={idx === form.integrationsSection.logosRow2.length - 1}
                        className="text-gray-500 p-1 rounded border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                      >
                        <FaArrowDown className="w-2.5 h-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLogo('logosRow2', idx)}
                        className="text-red-500 p-1 rounded border border-red-200 hover:bg-red-50"
                      >
                        <FaTrash className="w-2.5 h-2.5" />
                      </button>
                    </div>

                    <h4 className="font-bold text-xs text-gray-700 dark:text-slate-300">Logo #{idx + 1}</h4>

                    <div className="space-y-2">
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Alt text</label>
                        <input
                          className="input-modern text-xs py-1"
                          value={logo.alt || ''}
                          onChange={(e) => updateLogoField('logosRow2', idx, 'alt', e.target.value)}
                          placeholder="e.g. Stripe"
                        />
                      </div>
                      
                      <div className="space-y-1">
                        <label className="text-[10px] uppercase font-bold text-gray-400">Logo Image File</label>
                        <div className="flex gap-2 items-center">
                          <input
                            className="input-modern text-xs py-1 flex-1"
                            value={logo.src || ''}
                            onChange={(e) => updateLogoField('logosRow2', idx, 'src', e.target.value)}
                            placeholder="Image URL..."
                          />
                          <label className="flex items-center gap-1.5 px-2 py-1 border border-gray-300 dark:border-dark-600 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-dark-700 cursor-pointer whitespace-nowrap">
                            <FaUpload className="w-2.5 h-2.5" /> Upload
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleLogoUpload(e, 'logosRow2', idx)}
                              className="hidden"
                            />
                          </label>
                        </div>
                      </div>

                      {logo.src && (
                        <div className="relative w-full h-16 rounded-lg overflow-hidden border border-gray-200 bg-white flex items-center justify-center p-2">
                          <img
                            src={logo.src.startsWith('http') ? logo.src : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000'}/${logo.src}`}
                            alt="Logo preview"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tab 7: Landing FAQs */}
        {activeTab === 'faqs' && (
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-dark-700 pb-4">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Page FAQ Content</h2>
              <button
                onClick={addFAQ}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-bold text-primary-600 bg-primary-50 dark:bg-primary-950/20 hover:bg-primary-100 rounded-xl transition-colors"
              >
                <FaPlus className="w-3.5 h-3.5" /> Add FAQ Item
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Section Header Title</label>
                <input
                  className="input-modern"
                  value={form.faqSection.faqsTitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    faqSection: { ...prev.faqSection, faqsTitle: e.target.value }
                  }))}
                  placeholder="Frequently Asked Questions"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500">FAQ Section Subtitle</label>
                <input
                  className="input-modern"
                  value={form.faqSection.faqsSubtitle}
                  onChange={(e) => setForm(prev => ({
                    ...prev,
                    faqSection: { ...prev.faqSection, faqsSubtitle: e.target.value }
                  }))}
                  placeholder="Get answers to common questions..."
                />
              </div>
            </div>

            {/* List of FAQs */}
            <div className="space-y-4 pt-4">
              {form.faqSection.faqs.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 dark:border-dark-750 p-5 rounded-2xl relative space-y-3 bg-gray-50/20 dark:bg-dark-900/5">
                  <div className="absolute top-4 right-4 flex gap-1.5">
                    <button
                      onClick={() => moveItem('faqSection', 'faqs', idx, 'up')}
                      disabled={idx === 0}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowUp className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => moveItem('faqSection', 'faqs', idx, 'down')}
                      disabled={idx === form.faqSection.faqs.length - 1}
                      className="text-gray-500 p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30"
                    >
                      <FaArrowDown className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => removeFAQ(idx)}
                      className="text-red-500 p-1.5 rounded-lg border border-red-200 hover:bg-red-50"
                    >
                      <FaTrash className="w-3 h-3" />
                    </button>
                  </div>

                  <h3 className="font-bold text-sm text-gray-800 dark:text-slate-200">FAQ Item #{idx + 1}</h3>

                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Question</label>
                      <input
                        className="input-modern text-sm py-1.5 px-3"
                        value={faq.question}
                        onChange={(e) => updateFAQField(idx, 'question', e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] uppercase font-bold text-gray-400">Answer</label>
                      <textarea
                        className="input-modern text-sm py-1.5 px-3 min-h-[80px]"
                        value={faq.answer}
                        onChange={(e) => updateFAQField(idx, 'answer', e.target.value)}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold pt-1">
                    <input
                      type="checkbox"
                      checked={faq.isActive}
                      onChange={(e) => updateFAQField(idx, 'isActive', e.target.checked)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    Active & visible
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default HomeCMS;
